import type { AboutBuildInfo } from '../../components/scenarios/about/types'

import { isStageCapacitor, isStageTamagotchi } from '@proj-airi/stage-shared'
import { defineStore, storeToRefs } from 'pinia'
import { ref, watch } from 'vue'

import { useBuildInfo } from '../../composables/use-build-info'
import { useAuthStore } from '../auth'
import { useAiriCardStore } from '../modules/airi-card'
import { useConsciousnessStore } from '../modules/consciousness'
import { useSettingsAnalytics } from '../settings/analytics'
import {
  captureAnalyticsEvent,
  identifyAnalyticsUser,
  isAnalyticsAvailableInBuild,
  registerAnalyticsBuildInfo,
  resetAnalyticsIdentity,
  syncAnalyticsCapture,
} from './client'

export * from './client'
export * from './privacy-policy'

function analyticsSurface(): 'web' | 'desktop' | 'mobile' {
  return isStageTamagotchi()
    ? 'desktop'
    : isStageCapacitor()
      ? 'mobile'
      : 'web'
}

function providerMode(providerId: string | undefined): 'official' | 'custom' | 'unknown' {
  if (!providerId)
    return 'unknown'
  return providerId.startsWith('official-provider') ? 'official' : 'custom'
}

export const useSharedAnalyticsStore = defineStore('analytics-shared', () => {
  const buildInfo = ref<AboutBuildInfo>(useBuildInfo())
  const settingsAnalytics = useSettingsAnalytics()
  const { analyticsEnabled } = storeToRefs(settingsAnalytics)
  const isInitialized = ref(false)

  const appStartTime = ref<number | null>(null)
  const firstMessageTracked = ref(false)
  // In-memory only, intentionally — matches `firstMessageTracked` semantics
  // (resets on reload). PostHog can compute true "first time across all
  // sessions" with provider-side person-level dedup at query time.
  const firstModelSelectedTracked = ref(false)

  watch(analyticsEnabled, (enabled, previousEnabled) => {
    if (!isInitialized.value)
      return

    if (previousEnabled && !enabled) {
      captureAnalyticsEvent('settings_changed', {
        setting_name: 'analytics_enabled',
        previous_value: previousEnabled,
        new_value: enabled,
        source: 'settings',
        app_surface: analyticsSurface(),
      })
    }

    const shouldCapture = syncAnalyticsCapture(enabled)
    if (shouldCapture) {
      if (!previousEnabled && enabled) {
        captureAnalyticsEvent('settings_changed', {
          setting_name: 'analytics_enabled',
          previous_value: previousEnabled,
          new_value: enabled,
          source: 'settings',
          app_surface: analyticsSurface(),
        })
      }

      // When analytics is enabled mid-session, invalidate appStartTime and
      // mark first message as already tracked to avoid backfilling a stale
      // event with a misleading duration or timing.
      if (!previousEnabled && !firstMessageTracked.value) {
        appStartTime.value = null
        markFirstMessageTracked()
      }

      registerAnalyticsBuildInfo(buildInfo.value)
      // If a user enabled analytics mid-session while already authenticated,
      // identify them now — `initialize()`'s identify only fires once at
      // app startup and at auth-state changes, neither of which trigger
      // on a delayed opt-in. Without this, server-side `payment_completed`
      // (keyed by Better Auth user id) won't merge with the browser's
      // anonymous funnel events.
      const authStore = useAuthStore()
      if (authStore.isAuthenticated && authStore.user?.id)
        identifyAnalyticsUser(authStore.user.id)
    }
  })

  function initialize() {
    if (isInitialized.value)
      return

    appStartTime.value = Date.now()

    if (isAnalyticsAvailableInBuild()) {
      const shouldCapture = syncAnalyticsCapture(analyticsEnabled.value)
      if (shouldCapture) {
        registerAnalyticsBuildInfo(buildInfo.value)
        const platform = analyticsSurface()
        captureAnalyticsEvent('app_loaded', {
          platform,
          version: buildInfo.value.version,
        })
      }
    }

    // Wire PostHog identity to auth state. Without this server-side events
    // (`payment_completed` keyed on Better Auth `user.id`) and browser-side
    // funnel events (anonymous `distinct_id` until identify) live on
    // different person profiles and the funnel never joins.
    const authStore = useAuthStore()
    if (authStore.isAuthenticated && authStore.user?.id)
      identifyAnalyticsUser(authStore.user.id)

    authStore.onAuthenticated(() => {
      if (authStore.user?.id)
        identifyAnalyticsUser(authStore.user.id)
    })
    authStore.onLogout(() => {
      resetAnalyticsIdentity()
    })

    // Wire model-selection events. The consciousness store holds the
    // user-chosen chat model; both `activeProvider` and `activeModel` are
    // persisted via `useLocalStorageManualReset`, so on app load this
    // watcher fires once with the restored value as the "new" half (oldVal
    // is undefined). We guard on `oldProvider == null` to treat the boot
    // case as a baseline, not as a switch — otherwise every page load
    // would emit `model_switched`.
    //
    // Single `model_switched` callsite by design: consciousness reads/writes
    // happen across many UI surfaces (onboarding step, settings page,
    // model picker dropdown). Centralising the event here means new model-
    // change UI doesn't need to remember to fire analytics.
    const consciousness = useConsciousnessStore()
    watch(
      () => ({ provider: consciousness.activeProvider, model: consciousness.activeModel }),
      (next, prev) => {
        if (!next.provider || !next.model)
          return

        // Baseline on first watcher tick (oldVal undefined when the watcher
        // mounts with already-restored localStorage state).
        if (!prev) {
          if (!firstModelSelectedTracked.value) {
            // User has a model picked from a prior session — count it as
            // their first observed selection, but don't emit `model_switched`
            // since we have nothing to switch from. Only flip the dedup flag
            // when the capture actually went out (PostHog initialised + user
            // not opted out); otherwise an early opt-in or delayed init
            // would never get the chance to emit `first_model_selected`.
            const captured = captureAnalyticsEvent('first_model_selected', { model_id: next.model, provider: next.provider })
            if (captured)
              firstModelSelectedTracked.value = true
          }
          return
        }

        if (prev.provider === next.provider && prev.model === next.model)
          return

        if (!firstModelSelectedTracked.value) {
          // Same gating as the baseline branch: only mark first-selection
          // as tracked when capture actually shipped.
          const captured = captureAnalyticsEvent('first_model_selected', { model_id: next.model, provider: next.provider })
          if (captured)
            firstModelSelectedTracked.value = true
          return
        }

        // Genuine switch — emit only when we have a meaningful "from" model.
        // Provider transitions without a prior model (e.g. user clears then
        // re-selects) skip the switch event; the next clean A → B will fire.
        if (prev.provider && prev.provider !== next.provider) {
          captureAnalyticsEvent('provider_switched', {
            from_provider: prev.provider,
            to_provider: next.provider,
            from_provider_type: providerMode(prev.provider),
            to_provider_type: providerMode(next.provider),
            reason: 'manual',
            app_surface: analyticsSurface(),
          })
        }

        if (prev.model) {
          captureAnalyticsEvent('model_switched', {
            from_model: prev.model,
            to_model: next.model,
            reason: 'manual',
          })
          captureAnalyticsEvent('model_changed', {
            from_model: prev.model,
            to_model: next.model,
            provider: next.provider,
            reason: 'manual',
            app_surface: analyticsSurface(),
          })
        }
      },
      { immediate: true },
    )

    // Same single-callsite pattern as model_switched: track active
    // character changes here so any UI surface that swaps cards
    // (settings page, character picker, hotkey) is auto-instrumented.
    const cardStore = useAiriCardStore()
    watch(
      () => cardStore.activeCardId,
      (next, prev) => {
        // Boot-time watcher tick (no previous value) is the baseline,
        // not a switch — skip emit; the first real A→B will fire.
        if (!next || !prev || prev === next)
          return
        captureAnalyticsEvent('character_switched', {
          from_character_id: prev,
          to_character_id: next,
        })
      },
    )

    isInitialized.value = true
  }

  function markFirstMessageTracked() {
    firstMessageTracked.value = true
  }

  return {
    buildInfo,
    appStartTime,
    firstMessageTracked,
    initialize,
    markFirstMessageTracked,
  }
})
